import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../../api';
import BackendErrorMessage from '../../../components/BackendErrorMessage';

interface CandidatoResponse {
    candidatoId?: string;
    id?: string;
    nome: string;
    status: string;
}

interface SolicitarAlteracaoSensivelCandidatoModalProps {
    show: boolean;
    onHide: () => void;
    candidato: CandidatoResponse | null;
    processoId: string;
    onSaveSuccess: () => void;
}

interface SolicitacaoExistente {
    id: string;
    tipo: 'ALTERAR_STATUS' | 'DELETAR';
    status: 'PENDENTE' | 'APROVADO' | 'REJEITADO';
    statusAtual: string;
    statusSolicitado?: string;
    jiraTicket: string;
    dataCriacao: string;
}

const SolicitarAlteracaoSensivelCandidatoModal: React.FC<SolicitarAlteracaoSensivelCandidatoModalProps> = ({
    show,
    onHide,
    candidato,
    processoId,
    onSaveSuccess
}) => {
    const [tipo, setTipo] = useState<'ALTERAR_STATUS' | 'DELETAR'>('ALTERAR_STATUS');
    const [statusSolicitado, setStatusSolicitado] = useState('NAO_AVALIADO');
    const [jiraTicket, setJiraTicket] = useState('');
    const [saving, setSaving] = useState(false);
    const [carregandoHistorico, setCarregandoHistorico] = useState(false);
    const [solicitacoesExistentes, setSolicitacoesExistentes] = useState<SolicitacaoExistente[]>([]);

    useEffect(() => {
        const carregarSolicitacoesExistentes = async () => {
            if (!candidato) return;

            const candidatoId = candidato.candidatoId || candidato.id;
            if (!candidatoId) return;

            try {
                setCarregandoHistorico(true);
                const response = await api.get(`/processos/${processoId}/candidatos/${candidatoId}/alteracoes-sensiveis/solicitacoes`);
                setSolicitacoesExistentes(response.data || []);
            } catch (error) {
                setSolicitacoesExistentes([]);
                toast.error(BackendErrorMessage(error));
            } finally {
                setCarregandoHistorico(false);
            }
        };

        if (show && candidato) {
            setTipo('ALTERAR_STATUS');
            setStatusSolicitado(candidato.status || 'NAO_AVALIADO');
            setJiraTicket('');
            carregarSolicitacoesExistentes();
        } else {
            setSolicitacoesExistentes([]);
        }
    }, [show, candidato]);

    const formatarData = (data: string) =>
        new Date(data).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

    const solicitacaoPendente = solicitacoesExistentes.find((solicitacao) => solicitacao.status === 'PENDENTE');
    const ultimaSolicitacao = solicitacoesExistentes[0];
    const bloqueadoPorSolicitacaoPendente = !!solicitacaoPendente;

    const handleSubmit = async () => {
        if (!candidato) return;

        if (bloqueadoPorSolicitacaoPendente) {
            toast.info('Já existe uma solicitação pendente para este candidato.');
            return;
        }

        const candidatoId = candidato.candidatoId || candidato.id;
        if (!candidatoId) {
            toast.error('ID do candidato não encontrado');
            return;
        }

        if (!jiraTicket.trim()) {
            toast.error('Informe o ticket Jira.');
            return;
        }

        if (tipo === 'ALTERAR_STATUS' && statusSolicitado === candidato.status) {
            toast.error('Selecione um status diferente do atual.');
            return;
        }

        try {
            setSaving(true);
            await api.post(`/processos/${processoId}/candidatos/${candidatoId}/alteracoes-sensiveis/solicitacoes`, {
                tipo,
                statusSolicitado: tipo === 'ALTERAR_STATUS' ? statusSolicitado : undefined,
                jiraTicket: jiraTicket.trim()
            });

            toast.success('Solicitação enviada para aprovação.');
            onSaveSuccess();
            onHide();
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Solicitar Ação Sensível</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-2">
                    Esta ação depende de chamado no Jira e aprovação de um usuário ADMINISTRADOR.
                </p>
                <Alert variant="info" className="py-2">
                    A opção de deleção remove o Candidato.
                </Alert>
                {carregandoHistorico && (
                    <div className="mb-3">
                        <Spinner size="sm" className="me-2" />
                        Carregando solicitações já realizadas para este candidato...
                    </div>
                )}
                {!carregandoHistorico && solicitacaoPendente && (
                    <Alert variant="warning" className="py-2">
                        Já existe uma solicitação pendente para este candidato (Jira: {solicitacaoPendente.jiraTicket}, em {formatarData(solicitacaoPendente.dataCriacao)}).
                        Aguarde a decisão do ADMINISTRADOR antes de criar uma nova.
                    </Alert>
                )}
                {!carregandoHistorico && !solicitacaoPendente && ultimaSolicitacao && (
                    <Alert variant="secondary" className="py-2">
                        Última solicitação deste candidato: {ultimaSolicitacao.status} em {formatarData(ultimaSolicitacao.dataCriacao)} (Jira: {ultimaSolicitacao.jiraTicket}).
                    </Alert>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Tipo de solicitação</Form.Label>
                    <Form.Select
                        value={tipo}
                        onChange={(e) => setTipo(e.target.value as 'ALTERAR_STATUS' | 'DELETAR')}
                        disabled={bloqueadoPorSolicitacaoPendente}
                    >
                        <option value="ALTERAR_STATUS">Alterar status</option>
                        <option value="DELETAR">Solicitar deleção do Candidato</option>
                    </Form.Select>
                </Form.Group>

                {tipo === 'ALTERAR_STATUS' && (
                    <Form.Group className="mb-3">
                        <Form.Label>Status solicitado</Form.Label>
                        <Form.Select
                            value={statusSolicitado}
                            onChange={(e) => setStatusSolicitado(e.target.value)}
                            disabled={bloqueadoPorSolicitacaoPendente}
                        >
                            <option value="NAO_AVALIADO">Não avaliado</option>
                            <option value="INCONCLUSIVO">Inconclusivo</option>
                            <option value="AUSENTE">Ausente</option>
                            <option value="VALIDADO">Validado</option>
                            <option value="NAO_VALIDADO">Não Validado</option>
                        </Form.Select>
                    </Form.Group>
                )}

                <Form.Group>
                    <Form.Label>Ticket Jira *</Form.Label>
                    <Form.Control
                        type="text"
                        value={jiraTicket}
                        onChange={(e) => setJiraTicket(e.target.value)}
                        placeholder="PROJ-1234"
                        disabled={bloqueadoPorSolicitacaoPendente}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={saving || carregandoHistorico || bloqueadoPorSolicitacaoPendente}>
                    {saving ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Enviando...
                        </>
                    ) : (
                        'Enviar solicitação'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SolicitarAlteracaoSensivelCandidatoModal;
